import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCompleteEntityTemplateData } from '@/services/supabase/entity-templates';
import { supabase } from '@/services/supabase/supabase';
import { entityKeys } from './entities-queries';

// Define cache times - using the same aggressive caching as other queries
const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;
const STALE_TIME = DAY_IN_MS * 30; // 30 days stale time
const GC_TIME = DAY_IN_MS * 60; // 60 days garbage collection

// Define query keys for better cache management
export const entityTemplateKeys = {
  all: ['entityTemplates'] as const,
  details: () => [...entityTemplateKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...entityTemplateKeys.details(), String(id)] as const,
  fieldValues: (entityId: number | string) =>
    [...entityTemplateKeys.detail(entityId), 'fieldValues'] as const,
};

/**
 * Hook to fetch complete entity template data including fields and values
 * @param entityId The entity ID to fetch template data for
 * @param options Additional options for the query
 */
export function useEntityTemplateData(
  entityId: number | string | undefined,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: entityTemplateKeys.detail(entityId || ''),
    queryFn: async () => {
      if (!entityId) throw new Error('Entity ID is required');

      // Use the server-side function for efficient data fetching get_entity_template_data get_entity_data_with_metadata
      const { data: templateData, error: templateError } = await supabase.rpc('get_entity_template_data', {
        p_entity_id: entityId
      });

      if (templateError) {
        console.error('Error fetching entity template data:', templateError);

        // Handle specific error cases
        if (templateError.message === 'Entity not found or access denied') {
          console.warn('Entity not found or access denied, returning empty data structure');
          // Return a minimal valid data structure instead of throwing
          return {
            entity: null,
            template: null,
            sections: [],
            fieldsWithoutSection: []
          };
        }

        // For other errors, still throw
        throw templateError;
      }

      // Fetch the latest field_values JSON from the entity
      const { data: entityData, error: entityError } = await supabase
        .from('entities')
        .select('field_values')
        .eq('id', entityId)
        .single();
      
      if (entityError) {
        console.error('Error fetching entity field values:', entityError);
        // Don't throw here, just proceed with RPC values if this fails (fallback)
      } else {
        const jsonValues = entityData?.field_values || {};
        
        // Helper to update field value from JSON
        const updateFieldValue = (field: any) => {
          if (field && field.field_name && jsonValues[field.field_name] !== undefined) {
             field.value = jsonValues[field.field_name];
          }
        };

        // Update fields without section
        if (templateData.fieldsWithoutSection && Array.isArray(templateData.fieldsWithoutSection)) {
          templateData.fieldsWithoutSection.forEach(updateFieldValue);
        }

        // Update fields in sections
        if (templateData.sections && Array.isArray(templateData.sections)) {
          templateData.sections.forEach((section: any) => {
            if (section.template_fields && Array.isArray(section.template_fields)) {
              section.template_fields.forEach(updateFieldValue);
            }
          });
        }
      }

      return templateData;
    },
    enabled: !!entityId && (options.enabled !== false),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to update entity field values in bulk using the server-side function
 */
export function useUpdateEntityFieldValues() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      entityId,
      fieldValues
    }: {
      entityId: number | string;
      fieldValues: Record<string, any>;
    }) => {
      // Optimized client-side update (JSON-only)
      // Expects fieldValues keys to be Field Names
      
      if (Object.keys(fieldValues).length === 0) return true;

      // 1. Fetch current entity values (for safe merge)
      const { data: entityData, error: entityError } = await supabase
        .from('entities')
        .select('field_values')
        .eq('id', entityId)
        .single();
      
      if (entityError) throw entityError;

      // 2. Merge values
      const currentJson = entityData.field_values || {};
      const newJson = { ...currentJson };

      // Update with new values (keys are already names)
      Object.assign(newJson, fieldValues);

      // 3. Update entity
      const { data, error } = await supabase
        .from('entities')
        .update({ 
          field_values: newJson,
          updated_at: new Date().toISOString()
        })
        .eq('id', entityId)
        .select();

      if (error) {
        console.error('Error updating entity field values:', error);
        throw error;
      }

      return data;
    },
    onSuccess: async (_, variables) => {
      // Force immediate refetch of the data to ensure UI updates
      // invalidateQueries might be too lazy given the long staleTime
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: entityTemplateKeys.detail(variables.entityId),
        }),
        queryClient.refetchQueries({
          queryKey: entityKeys.detail(variables.entityId),
        })
      ]);
    },
  });
}

/**
 * Hook to sync entity fields with template fields
 * This will find all template fields that don't have corresponding entity field values
 * and create new entity field values for those fields with default values
 */
export function useSyncEntityFields() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ entityId }: { entityId: number | string }) => {
      // Call the server-side function for syncing fields
      const { data, error } = await supabase.rpc('sync_entity_template_fields', {
        p_entity_id: entityId
      });

      if (error) {
        console.error('Error syncing entity fields:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate the entity template data query
      queryClient.invalidateQueries({
        queryKey: entityTemplateKeys.detail(variables.entityId),
      });

      // Also invalidate the entity detail query
      queryClient.invalidateQueries({
        queryKey: entityKeys.detail(variables.entityId),
      });
    },
  });
}

/**
 * Hook to create a new template section
 */
export function useCreateTemplateSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      name,
      entityId
    }: {
      templateId: number | string;
      name: string;
      entityId: number | string;
    }) => {
      // Validate inputs
      if (!templateId || templateId === 'undefined') {
        throw new Error('Valid template ID is required to create a section');
      }

      if (!entityId || entityId === 'undefined') {
        throw new Error('Valid entity ID is required to create a section');
      }

      // Import the createTemplateSection function
      const { createTemplateSection } = await import('@/services/supabase/templates');

      // Create the template section
      const section = await createTemplateSection({
        template_id: Number(templateId),
        name
      });

      return section;
    },
    onSuccess: async (_, variables) => {
      // Invalidate and refetch the entity template data query
      await queryClient.invalidateQueries({
        queryKey: entityTemplateKeys.detail(variables.entityId),
      });

      // Explicitly refetch the data
      await queryClient.refetchQueries({
        queryKey: entityTemplateKeys.detail(variables.entityId),
      });
    },
  });
}

/**
 * Hook to create a new template field with corresponding form field
 */
export function useCreateTemplateField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      sectionId,
      fieldData,
      entityId
    }: {
      templateId: number | string;
      sectionId?: number | null;
      fieldData: {
        field_name: string;
        field_type: string;
        is_required: boolean;
        default_value?: string | null;
        options?: string | string[] | null;
        help_text?: string | null;
        placeholder?: string | null;
      };
      entityId: number | string;
    }) => {
      // Validate inputs
      if (!templateId || templateId === 'undefined') {
        throw new Error('Valid template ID is required to create a field');
      }

      if (!entityId || entityId === 'undefined') {
        throw new Error('Valid entity ID is required to create a field');
      }

      // Import the necessary functions
      const { createTemplateField } = await import('@/services/supabase/templates');
      const { createFormField, getFormFieldByNameAndType } = await import('@/services/supabase/form-fields');
      const { setEntityFieldValue } = await import('@/services/supabase/entity-field-values');

      // Create the template field
      const templateField = await createTemplateField({
        template_id: Number(templateId),
        section_id: sectionId ? Number(sectionId) : null,
        ...fieldData
      });

      // Check if a form field with the same name and type already exists
      let formField = await getFormFieldByNameAndType(fieldData.field_name, fieldData.field_type);

      // If not, create a new form field
      if (!formField) {
        formField = await createFormField(fieldData);
      }

      // Create the entity field value
      await setEntityFieldValue(
        entityId,
        formField.id,
        fieldData.default_value || null
      );

      return { templateField, formField };
    },
    onSuccess: async (_, variables) => {
      // Invalidate and refetch the entity template data query
      await queryClient.invalidateQueries({
        queryKey: entityTemplateKeys.detail(variables.entityId),
      });

      // Explicitly refetch the data
      await queryClient.refetchQueries({
        queryKey: entityTemplateKeys.detail(variables.entityId),
      });
    },
  });
}
